import { InvoiceFromOcrDto } from "@/api/invoiceApi";
import { BankAccountDto, InvoiceFormData } from "@/types";
import { ocrBankAccountExists } from "@/utils/invoiceUtils";
import { Alert, BodyShort } from "@digital-services-gard-as/anchor-react";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Trans } from "react-i18next";
import { toOcrBankAccount } from "./utils";

type BankAccountAlertProps = {
    bankAccounts?: BankAccountDto[];
    ocrResponse?: InvoiceFromOcrDto;
};

export const BankAccountAlert = ({ bankAccounts, ocrResponse }: BankAccountAlertProps) => {
    const { watch } = useFormContext<InvoiceFormData>();
    const bankAccount = watch("bankAccount");

    const ocrBankAccount = useMemo(() => toOcrBankAccount(ocrResponse), [ocrResponse]);

    //if the accountnumber does not exists it is a new bank account.
    const hasOcrBankAccountNumber = Boolean(ocrBankAccount?.accountNumber);
    const isNewBankAccount = hasOcrBankAccountNumber && !ocrBankAccountExists(bankAccounts, ocrBankAccount);

    // if only account number or currency is present, and is not new we consider the OCR bank account as incomplete,
    // and show the missing OCR alert
    const hasFullOcrBankAccount = hasOcrBankAccountNumber && Boolean(ocrBankAccount?.currency);
    const showMissingOcrAlert = ocrResponse && !hasFullOcrBankAccount;

    const showAlert = [isNewBankAccount, showMissingOcrAlert].some(Boolean);

    const alertContent = useMemo(() => {
        const accountNumberDisplay = hasFullOcrBankAccount
            ? `${ocrBankAccount?.accountNumber} - ${ocrBankAccount?.currency}`
            : (ocrBankAccount?.accountNumber ?? "");

        switch (true) {
            case isNewBankAccount: {
                const textKey = bankAccount
                    ? "invoices:createInvoice.generalInfo.newBankAccountAlertText"
                    : "invoices:createInvoice.generalInfo.newBankAccountAlertTextNotSelected";

                return {
                    textKey,
                    values: { accountNumber: accountNumberDisplay }
                };
            }

            case showMissingOcrAlert:
                return {
                    textKey: "invoices:createInvoice.generalInfo.notFoundBankAccountAlertText" as const
                };

            default:
                return undefined;
        }
    }, [
        hasFullOcrBankAccount,
        ocrBankAccount?.accountNumber,
        ocrBankAccount?.currency,
        isNewBankAccount,
        showMissingOcrAlert,
        bankAccount
    ]);

    if (!showAlert) {
        return null;
    }

    return (
        <Alert variant={bankAccount && isNewBankAccount ? "warning" : "danger"} contentMaxWidth>
            <BodyShort>
                <Trans i18nKey={alertContent?.textKey} values={alertContent?.values} />
            </BodyShort>
        </Alert>
    );
};
